import * as React from "react"
import { useNavigate } from "react-router-dom"
import { Eye, EyeOff, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/lib/auth"
import { ApiError } from "@/lib/api"
import { isFirebaseConfigured } from "@/lib/firebase"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

import {
  CruceDePortada,
  GoogleMark,
  Portada,
  Separador,
  traducirError,
} from "@/routes/login/Portada"
import { darDeAltaEmpresa } from "@/routes/registro/api"

/**
 * DAR DE ALTA UNA EMPRESA.
 *
 * La otra mitad del par. `LoginPage` es para quien ya tiene un sitio adonde
 * entrar; ésta es para quien viene a crearlo, y por eso son dos páginas y no
 * dos pestañas: **hacen cosas distintas**. Entrar no escribe nada; esto crea un
 * cliente, su plan de cuentas gerencial, su mapeo contra los cuatro planes
 * locales, sus reglas de tratamiento y tu rol dentro. Una pantalla que crea
 * medio producto no puede parecer la misma que una que sólo comprueba una
 * contraseña.
 *
 * ===========================================================================
 * SON DOS LLAMADAS, Y NO SE PUEDEN JUNTAR
 * ===========================================================================
 *
 *   1. Firebase crea la IDENTIDAD (`signUpWithEmail`, o la ventana de Google).
 *   2. El backend crea la EMPRESA (`POST /auth/registro`), que necesita el
 *      token ya verificado del paso 1 para saber a quién darle el rol.
 *
 * El orden no es negociable y **el primero puede triunfar y el segundo fallar**:
 * quedaría una cuenta de Firebase sin empresa. Eso NO se trata como un error a
 * secas, se trata diciendo la verdad —«tu cuenta quedó creada; lo que falló fue
 * el alta de la empresa»— y dejando el botón listo para reintentar sólo el
 * segundo paso. Volver a crear la identidad daría `auth/email-already-in-use` y
 * parecería que el problema es el correo, cuando el correo es lo único que salió
 * bien.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTA PÁGINA **NO** PREGUNTA: EL RUC, EL PAÍS Y EL RÉGIMEN
 *
 * Son el paso 1 del traspaso, y ahí se quedan. Allí están explicados —«del
 * documento oficial: ficha RUC, constancia de situación fiscal, cartão CNPJ o
 * aviso de operación. No de memoria y no de una factura vieja»— y el régimen se
 * valida contra el catálogo del rulepack del país. Aquí no habría nada de eso.
 *
 * Y el país **no se puede cambiar después**: decide el rulepack, el plan de
 * cuentas y el calendario. Es justo el dato que no debe contestarse con prisa en
 * un formulario de registro, con el pulgar, antes de saber qué es el producto.
 *
 * Así que el alta deja la empresa lista y vacía, y el traspaso —que es una
 * pantalla hecha para preguntar— pregunta. Ver `backend/app/modules/auth/registro.py`.
 */
export function RegistroPage() {
  const { signUpWithEmail, signInWithGoogle, refreshProfile } = useAuth()
  const navigate = useNavigate()

  const [empresa, setEmpresa] = React.useState("")
  const [nombre, setNombre] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [verClave, setVerClave] = React.useState(false)
  const [pending, setPending] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  /*
    LA IDENTIDAD YA ESTÁ CREADA Y EL ALTA NO. Es el estado intermedio del que
    habla la cabecera, y tiene que ser un estado y no un mensaje: mientras valga
    `true`, el botón reintenta SÓLO la segunda llamada.
  */
  const [identidadLista, setIdentidadLista] = React.useState(false)

  const empresaValida = empresa.trim().length >= 2

  /** El paso 2, aislado, porque es el que se reintenta solo. */
  async function crearLaEmpresa() {
    const alta = await darDeAltaEmpresa(empresa, nombre)
    // El perfil que hay en memoria es el de antes del alta: sin alcances. Sin
    // volver a pedirlo, `Entrada` decidiría con la cartera vacía y mandaría al
    // dashboard de un cliente que sí existe — la aplicación se vería vacía y
    // parecería que el registro no funcionó.
    await refreshProfile()
    return alta
  }

  function contarElFallo(err: unknown) {
    if (err instanceof ApiError && typeof err.detail === "string") return err.detail
    if (identidadLista) {
      return (
        "Tu cuenta quedó creada, pero no pude dar de alta la empresa. " +
        "Vuelve a pulsar «Crear la empresa»: no hace falta registrarse otra vez."
      )
    }
    return traducirError(err)
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!empresaValida) return
    setPending(true)
    setError(null)
    try {
      if (!identidadLista) {
        await signUpWithEmail(email, password)
        setIdentidadLista(true)
      }
      await crearLaEmpresa()
      // A la raíz: `Entrada` ve que no hay ni una sociedad y manda al traspaso.
      // Decidirlo aquí sería un segundo sitio donde mantener la misma regla.
      navigate("/", { replace: true })
    } catch (err) {
      setError(contarElFallo(err))
    } finally {
      setPending(false)
    }
  }

  async function handleGoogle() {
    if (!empresaValida) {
      setError("Escribe primero el nombre de tu empresa: es lo que voy a dar de alta.")
      return
    }
    setPending(true)
    setError(null)
    try {
      if (!identidadLista) {
        await signInWithGoogle()
        setIdentidadLista(true)
      }
      await crearLaEmpresa()
      navigate("/", { replace: true })
    } catch (err) {
      setError(contarElFallo(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Portada
      titulo={t("Crear cuenta")}
      bajada={t("Doy de alta tu empresa y te dejo dentro, con su plan de cuentas listo.")}
      error={error}
      pie={
        <>
          <CruceDePortada
            pregunta={t("¿Ya tienes cuenta?")}
            accion={t("Iniciar sesión")}
            a="/login"
          />
          {/*
            EL DESCARGO VIVE AQUÍ, y no en el login. Dice dónde acaba lo que hace
            el producto, y el sitio donde eso importa es donde alguien decide
            empezar a usarlo — no donde vuelve a entrar cada mañana.
          */}
          <p className="mt-5 text-center text-2xs leading-4 text-muted-foreground">
            {t("La firma de una declaración es responsabilidad de una persona colegiada. Marty prepara el trabajo entero; la firma la pone quien puede ponerla.")}
          </p>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-3.5">
        {/*
          LA EMPRESA VA PRIMERA, y no el correo. Es lo que distingue esta página
          de la de entrar: el primer campo dice qué estás haciendo aquí. Con el
          correo arriba, las dos pantallas empiezan igual.
        */}
        <div className="space-y-1.5">
          <Label htmlFor="empresa">{t("Tu empresa")}</Label>
          <Input
            id="empresa"
            value={empresa}
            onChange={(event) => setEmpresa(event.target.value)}
            required
            autoFocus
            placeholder={t("Andina Servicios SAC")}
            className="h-10"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="nombre">{t("Tu nombre")}</Label>
          <Input
            id="nombre"
            value={nombre}
            onChange={(event) => setNombre(event.target.value)}
            autoComplete="name"
            placeholder={t("Rosa Quispe")}
            className="h-10"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email">{t("Correo")}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required={isFirebaseConfigured}
            disabled={identidadLista}
            placeholder={t("contador@estudio.pe")}
            className="h-10"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">{t("Contraseña")}</Label>
          <div className="relative">
            <Input
              id="password"
              type={verClave ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required={isFirebaseConfigured}
              disabled={identidadLista}
              className="h-10 pr-10"
            />
            <button
              type="button"
              onClick={() => setVerClave((v) => !v)}
              aria-pressed={verClave}
              aria-label={verClave ? "Ocultar la contraseña" : "Mostrar la contraseña"}
              title={verClave ? "Ocultar la contraseña" : "Mostrar la contraseña"}
              className={cn(
                "absolute inset-y-0 right-0 grid w-10 place-items-center rounded-r-md",
                "text-muted-foreground transition-colors hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              {verClave ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {/*
            El mínimo de Firebase, dicho ANTES de fallar. Enterarse de que son
            seis caracteres por un mensaje en rojo después de pulsar es
            enterarse tarde.
          */}
          {!identidadLista ? (
            <p className="text-2xs text-muted-foreground">{t("Mínimo seis caracteres.")}</p>
          ) : null}
        </div>

        <Button
          type="submit"
          size="lg"
          className="h-11 w-full"
          disabled={pending || !empresaValida}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {identidadLista ? t("Crear la empresa") : t("Crear cuenta")}
        </Button>
      </form>

      {/*
        GOOGLE, DEBAJO Y NO ENCIMA — al revés que en el login, y a propósito.
        Allí es el único camino que funciona. Aquí el formulario lleva un dato
        que Google no sabe dar —el nombre de la empresa—, así que el orden
        natural es rellenarlo y luego elegir con qué identidad firmarlo.
      */}
      {!identidadLista ? (
        <>
          <Separador>{t("o")}</Separador>
          <Button
            variant="outline"
            size="lg"
            className="h-10 w-full justify-center gap-2.5 text-sm"
            onClick={handleGoogle}
            disabled={pending}
          >
            <GoogleMark className="h-4 w-4" />
            {t("Continuar con Google")}
          </Button>
        </>
      ) : null}
    </Portada>
  )
}
